/* NALLI — companion JS for blocks/nalli-buy-buttons.liquid (WS7).
 *
 * Responsibilities (ported from live blocks/buy-buttons.liquid inline scripts,
 * re-validated against the Horizon 4.1.3 product-form pipeline):
 *   1. Jewellery enquiry modal (validation + Make.com webhook POST).
 *   2. Voucher isolation: reveal the pre-rendered Liquid error when #dataform === "0"
 *      (the hard submit block lives in nalli-voucher-gate.js).
 *   3. Saree Fall & Edging checkbox: price swap, stock cap, warning copy.
 *   4. Priority multi-variant allocation: distributes the requested quantity
 *      across variants ("store locations") by shop.metafields.inventory.priority,
 *      zeroing variants already in the cart (read from WS8's `.cart_current_data`
 *      JSON div in the cart drawer). When the allocation is NOT the trivial
 *      "one line on the selected variant", the submit is intercepted
 *      (capture phase) and posted as a JSON multi-line /cart/add, dispatching
 *      the same CartLinesUpdateEvent contract core product-form.js uses, so the
 *      cart drawer / bubble / WS8 shipping-note writer all react normally.
 *
 * Live's `.extra-inputs` hidden `items[N][...]` inputs are deliberately NOT
 * reproduced: 4.1.3's core form submits the full FormData including the default
 * id/quantity pair, so bracket inputs would double-add. See rebuild/bugfixes/ws7.md.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[6-9]\d{9}$/; // 10-digit Indian mobile number

/* ------------------------------------------------------------------ *
 * 1. Jewellery enquiry modal
 * ------------------------------------------------------------------ */
function initEnquiry() {
  const wrapper = document.querySelector('[data-nalli-enquiry]');
  if (!wrapper) return;

  const modal = wrapper.querySelector('#loginModal');
  const openBtn = wrapper.querySelector('#member-login-btn');
  const closeBtn = wrapper.querySelector('#closeModal');
  const submitBtn = wrapper.querySelector('#loginSubmit');
  const errorEl = wrapper.querySelector('#loginError');
  const emailInput = wrapper.querySelector('#memberEmail');
  const phoneInput = wrapper.querySelector('#memberPhone');
  const emailErrorEl = wrapper.querySelector('#emailError');
  const phoneErrorEl = wrapper.querySelector('#phoneError');
  const successEl = wrapper.querySelector('#loginSuccess');
  const webhookUrl = wrapper.dataset.webhook;

  if (!modal || !openBtn || !submitBtn) return;

  const detailsContent = () =>
    document.querySelector('.product-details .group-block .group-block-content');

  openBtn.addEventListener('click', () => {
    modal.classList.remove('hidden');
    detailsContent()?.classList.add('enquiry-open');
  });

  closeBtn?.addEventListener('click', () => {
    modal.classList.add('hidden');
    detailsContent()?.classList.remove('enquiry-open');
  });

  function showFieldError(inputEl, el, message) {
    inputEl?.classList.add('invalid');
    if (el) {
      el.textContent = message;
      el.classList.remove('hidden');
    }
  }

  function clearFieldError(inputEl, el) {
    inputEl?.classList.remove('invalid');
    if (el) {
      el.textContent = '';
      el.classList.add('hidden');
    }
  }

  function validateEmail() {
    const value = emailInput?.value.trim() ?? '';
    if (!value) {
      showFieldError(emailInput, emailErrorEl, 'Email ID is required.');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      showFieldError(emailInput, emailErrorEl, 'Please enter a valid email address.');
      return false;
    }
    clearFieldError(emailInput, emailErrorEl);
    return true;
  }

  function validatePhone() {
    const value = phoneInput?.value.trim() ?? '';
    if (!value) {
      showFieldError(phoneInput, phoneErrorEl, 'Mobile number is required.');
      return false;
    }
    if (!PHONE_REGEX.test(value)) {
      showFieldError(phoneInput, phoneErrorEl, 'Please enter a valid 10-digit mobile number.');
      return false;
    }
    clearFieldError(phoneInput, phoneErrorEl);
    return true;
  }

  emailInput?.addEventListener('input', () => {
    if (emailInput.classList.contains('invalid')) validateEmail();
  });
  phoneInput?.addEventListener('input', () => {
    if (phoneInput.classList.contains('invalid')) validatePhone();
  });
  emailInput?.addEventListener('blur', validateEmail);
  phoneInput?.addEventListener('blur', validatePhone);

  submitBtn.addEventListener('click', async () => {
    if (errorEl) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
    }
    const isEmailValid = validateEmail();
    const isPhoneValid = validatePhone();
    if (!isEmailValid || !isPhoneValid || !webhookUrl) return;

    try {
      await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          MAIL_ID: emailInput.value.trim(),
          MOBILE_NO: phoneInput.value.trim(),
        }),
      });
      if (successEl) {
        successEl.textContent = 'Thank you! Your details have been submitted successfully.';
        successEl.classList.remove('hidden');
        successEl.style.color = 'green';
      }
      submitBtn.classList.add('btn-success');
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.classList.remove('hidden');
      }
    }
  });
}

/* ------------------------------------------------------------------ *
 * Shared helpers
 * ------------------------------------------------------------------ */
function readJson(el) {
  if (!el || !el.textContent?.trim()) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[nalli-buy-buttons] could not parse JSON blob:', e);
    return null;
  }
}

/** Read WS8's `.cart_current_data` JSON dump (array of { id, title, qty }). */
function readCartCurrentData() {
  const cartDiv = document.querySelector('.cart_current_data');
  if (!cartDiv || cartDiv.textContent.trim() === '') return [];
  try {
    const cleaned = cartDiv.textContent
      .replace(/(\r\n|\n|\r)/gm, '')
      .replace(/'/g, '"')
      .replace(/,\s*]/g, ']')
      .replace(/,\s*}/g, '}');
    const parsed = JSON.parse(cleaned);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn('[nalli-buy-buttons] could not parse cart_current_data:', e);
    return [];
  }
}

/* ------------------------------------------------------------------ *
 * 2–4. Voucher reveal, fall & edging, allocation — per form component
 * ------------------------------------------------------------------ */
function initForm(component) {
  const form = component.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return;

  // --- 2. Voucher isolation: reveal the pre-rendered message on load ---
  const dataform = component.querySelector('#dataform');
  if (dataform && dataform.textContent.trim() === '0') {
    component.querySelector('.product-form-text__error')?.classList.remove('hidden');
  }

  // --- Allocation data ---
  const variantStock = readJson(component.querySelector('[data-nalli-variant-stock]')) || [];
  const priorityMap = readJson(component.querySelector('[data-nalli-priority-map]')) || {};
  const fallInput = component.querySelector('#fall-and-edging');
  const fabricLengthInput = component.querySelector('#fabriclength');
  const quantityInput = component.querySelector('quantity-selector-component input[name="quantity"]');

  // Zero out variants already in the cart (they must not receive new allocation —
  // live parity: an in-cart variant's remaining stock is treated as unavailable).
  const cartVariantIds = new Set(readCartCurrentData().map((item) => String(item.id)));
  for (const v of variantStock) {
    if (cartVariantIds.has(String(v.id))) v.qty = 0;
  }

  const fallSelected = () => {
    if (!fallInput) return false;
    return fallInput.value.trim() !== '';
  };

  function distributeQuantity(requestedQty) {
    let remaining = requestedQty;
    const allocations = {};
    let availableStock = [...variantStock];
    if (fallSelected()) {
      availableStock = availableStock.filter((v) => v.fall);
    }
    const sortedVariants = availableStock.sort(
      (a, b) => (priorityMap[a.title] || 999) - (priorityMap[b.title] || 999)
    );
    for (const variant of sortedVariants) {
      if (remaining <= 0) break;
      const allocate = Math.min(variant.qty, remaining);
      if (allocate > 0) {
        allocations[variant.id] = allocate;
        remaining -= allocate;
      }
    }
    return allocations;
  }

  // --- 3. Fall & Edging checkbox ---
  const checkbox = component.querySelector('#product-fall_pico');
  if (checkbox) {
    checkbox.addEventListener('change', function () {
      const warnings = component.querySelector('.quantity-warning');
      const plusBtn = component.querySelector('.quantity-plus');
      const priceContainer = document.querySelector(
        '.product-information__grid .price-container .price'
      );

      if (quantityInput) quantityInput.value = '1';
      plusBtn?.removeAttribute('disabled');

      if (this.checked) {
        if (fallInput) fallInput.value = 'Included';
        const fpValue = parseInt(this.dataset.fp, 10);
        if (quantityInput) {
          quantityInput.max = fpValue;
          quantityInput.min = 1;
          quantityInput.dataset.productmax = String(fpValue);
        }
        if (warnings) {
          warnings.textContent = `Only ${fpValue} pieces available in stock with fall and pico customization`;
        }
        if (priceContainer) priceContainer.innerHTML = this.dataset.customization;
      } else {
        if (fallInput) fallInput.value = '';
        const totalValue = parseInt(this.dataset.total, 10);
        if (quantityInput) {
          quantityInput.max = totalValue;
          quantityInput.min = 1;
          quantityInput.dataset.productmax = String(totalValue);
        }
        if (warnings) {
          warnings.textContent = `Only ${totalValue} pieces available in stock`;
        }
        if (priceContainer) priceContainer.innerHTML = this.dataset.price;
      }
      if (warnings) warnings.style.display = 'block';
    });
  }

  // --- 4. Multi-line allocation submit interception (capture phase) ---
  if (variantStock.length === 0) return;

  form.addEventListener(
    'submit',
    (event) => {
      // Voucher gate (nalli-voucher-gate.js, bound on document capture) runs
      // first and stops propagation when blocked — reaching here means allowed.
      const requestedQty = parseInt(quantityInput?.value ?? '1', 10) || 1;
      const allocations = distributeQuantity(requestedQty);
      const allocationEntries = Object.entries(allocations);
      const selectedVariantId = form.querySelector('input[name="id"]')?.value;

      const isTrivial =
        allocationEntries.length === 0 ||
        (allocationEntries.length === 1 &&
          String(allocationEntries[0][0]) === String(selectedVariantId) &&
          allocationEntries[0][1] === requestedQty);

      // Trivial case (single line on the selected variant, or no stock data):
      // let core 4.1.3 product-form.js handle it — properties inputs
      // (Length / Fall And Edging) ride along in its FormData.
      if (isTrivial) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const fallEnabled = fallSelected();
      const lengthEnabled = fabricLengthInput && fabricLengthInput.value.trim() !== '';

      const items = allocationEntries.map(([variantId, qty]) => {
        const properties = {};
        const variant = variantStock.find((v) => String(v.id) === String(variantId));
        if (variant?.fall && fallEnabled) properties['Fall And Edging'] = 'Included';
        if (lengthEnabled) properties['Length'] = fabricLengthInput.value.trim();
        const item = { id: Number(variantId), quantity: qty };
        if (Object.keys(properties).length > 0) item.properties = properties;
        return item;
      });

      submitMultiLineAdd(component, items, requestedQty);
    },
    true
  );
}

/** JSON multi-line /cart/add mirroring core product-form.js's event contract. */
function submitMultiLineAdd(component, items, itemCount) {
  const errorEl = component.querySelector('[ref="addToCartTextError"]');
  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  component.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  const refreshCart = () =>
    fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

  fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') }),
  })
    .then((response) => response.json())
    .then(async (response) => {
      if (response.status) {
        // Error path — surface the message like core does.
        if (errorEl) {
          errorEl.classList.remove('hidden');
          const textNode = errorEl.childNodes[2];
          const message = response.message || 'Add to cart failed';
          if (textNode) textNode.textContent = message;
          else errorEl.appendChild(document.createTextNode(message));
        }
        const ajaxCart = await refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'product-form-component',
            sourceId: component.id ? component.id.toString() : '',
            itemCount,
            productId: component.dataset.productId,
          },
        });
        return;
      }

      errorEl?.classList.add('hidden');
      const ajaxCart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'product-form-component',
          sourceId: component.id ? component.id.toString() : '',
          itemCount,
          productId: component.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
    })
    .catch((error) => {
      console.error('[nalli-buy-buttons] multi-line add failed:', error);
      deferred.reject(error);
    });
}

/* ------------------------------------------------------------------ */
function init() {
  initEnquiry();
  document
    .querySelectorAll('.nalli-buy-buttons product-form-component, .nalli-buy-buttons readymade-product-form-component')
    .forEach(initForm);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
